/* Chart tiles in IndexedDB, and why they are no longer in a service worker.
 *
 * ═══ THE SERVICE WORKER WAS NOT CACHING ANYTHING ═══
 *
 * Measured on the boat, 4 Aug 2026, with the debug panel: `sw` 0 ms and 0%
 * cache hits on EVERY request. Not degraded — absent. A service worker needs a
 * SECURE CONTEXT to register, and the pi is reached over plain
 * `http://openplotter.local:3000` on the boat's own network as often as over
 * the Tailscale https name. So on that device there was no chart cache at all:
 * every pan refetched squares already downloaded, and the six connections a
 * browser gives one host were spent re-fetching them. Nothing said so.
 *
 * `.local` names are not on the "potentially trustworthy origin" list and never
 * will be — mDNS is spoofable, so any device on the LAN could answer to the
 * name, which is exactly what the secure-context rule exists to prevent.
 *
 * IndexedDB has no such gate. It works identically on both transports, which is
 * the entire reason for this file.
 *
 * (The Cache API is gated too, so doing it by hand outside a worker was never
 * an option either. IndexedDB is the only durable store available on http.)
 *
 * ═══ WHAT WAS GIVEN UP, DELIBERATELY ═══
 *
 * The service worker also served the app SHELL offline, and nothing here can do
 * that: loading index.html is a navigation request and only a service worker
 * can answer one. So with the pi unreachable the app no longer opens.
 *
 * That is the owner's call and it is an honest trade: THE APP IS SERVED BY THE
 * PI. If the pi is down the page cannot load anyway, so the shell cache only
 * ever covered one case — a device that had the app open before, now away from
 * the boat entirely, planning from a café. Not a case this boat needs, and it
 * was costing the chart cache on every device that used plain http.
 */

const DB = 'gd-tiles';
const STORE = 'tiles';

/* Through the pi tiles arrive as lossless WebP at about 10 kB rather than 26 kB
 * of PNG, so this is roughly 40 MB — comfortably inside what a browser gives an
 * origin, and more chart than the 2 500 the service worker held.
 *
 * IT SHARES A QUOTA WITH THE DEM CACHE (`gd-terrain`, 1 200 tiles at ~250 kB).
 * Two stores on one origin budget: sized together on purpose rather than each
 * assuming it has the disk to itself. */
export const MAX_TILES = 4000;
/** Let it overshoot this far before trimming, so a pan is not a trim per tile. */
const TRIM_SLACK = 250;

interface Row {
  k: string;
  buf: ArrayBuffer;
  type: string;
  used: number;
}

let dbp: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  dbp ??= new Promise((resolve, reject) => {
    const rq = indexedDB.open(DB, 1);
    rq.onupgradeneeded = () => {
      const s = rq.result.createObjectStore(STORE, { keyPath: 'k' });
      s.createIndex('used', 'used');
    };
    rq.onsuccess = () => resolve(rq.result);
    rq.onerror = () => reject(rq.error);
  });
  return dbp;
}

/**
 * Ask the browser to keep this data.
 *
 * SAFARI EVICTS INDEXEDDB AFTER ABOUT SEVEN DAYS UNUSED. A boat that goes a
 * fortnight between sails would come back to an empty chart cache and no way to
 * tell that from "the cache never worked" — which is precisely the failure this
 * file replaced. One call, and trivially forgotten.
 *
 * Best-effort by design: it can be refused, and being refused is not a reason
 * to stop caching. It just means eviction is possible again.
 *
 * ═══ AND IT IS A NO-OP OVER PLAIN HTTP. MEASURED ON THE DEPLOYED APP. ═══
 *
 * `navigator.storage` is UNDEFINED at http://<ip>:3000 — `StorageManager` is
 * itself `[SecureContext]`, the same gate that took the service worker away and
 * the whole reason this file exists. So the one case M14 was built to serve is
 * the one case that gets no eviction protection.
 *
 * This does NOT undo M14. The cache itself works on plain http — verified: a
 * reload drew the chart with zero tile requests, with `caches` not even present
 * in `globalThis`. Only the durability guarantee is missing, and the difference
 * that makes in practice is that reaching the dash by its Tailscale https name
 * earns a cache that survives a fortnight ashore and reaching it by IP does not.
 *
 * Do not "fix" this by reaching for another API. There isn't one; the gate is
 * the point. It is a thing to SAY in the UI, not to work around.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function get(k: string): Promise<{ buf: ArrayBuffer; type: string } | null> {
  try {
    const d = await db();
    return await new Promise((resolve) => {
      const tx = d.transaction(STORE, 'readwrite');
      const rq = tx.objectStore(STORE).get(k);
      rq.onsuccess = () => {
        const v = rq.result as Row | undefined;
        if (!v) return resolve(null);
        // Touch it, so a trim keeps the water actually being sailed over rather
        // than whatever happened to be written last.
        tx.objectStore(STORE).put({ ...v, used: Date.now() });
        resolve({ buf: v.buf, type: v.type });
      };
      rq.onerror = () => resolve(null);
    });
  } catch {
    /* A private window, a full disk, a browser refusing IndexedDB: none of
     * these should cost you the chart. Fall through and fetch. */
    return null;
  }
}

let trimming = false;

export async function put(k: string, buf: ArrayBuffer, type: string): Promise<void> {
  try {
    const d = await db();
    const tx = d.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ k, buf, type, used: Date.now() } satisfies Row);
    /* COUNTED, NOT ENUMERATED. The service worker version called `keys()` after
     * every put — measured at ~100 ms on a full 2 500-entry cache, per tile,
     * for a trim it was almost never going to do. `count()` is an index lookup. */
    if (trimming) return;
    const c = tx.objectStore(STORE).count();
    c.onsuccess = () => {
      if (c.result <= MAX_TILES + TRIM_SLACK) return;
      trimming = true;
      let over = c.result - MAX_TILES;
      const cur = tx.objectStore(STORE).index('used').openCursor();
      cur.onsuccess = () => {
        const cc = cur.result;
        if (!cc || over <= 0) {
          trimming = false;
          return;
        }
        cc.delete();
        over--;
        cc.continue();
      };
      cur.onerror = () => {
        trimming = false;
      };
    };
  } catch {
    /* Over quota or refused. The tile was still served; it just will not be
     * there next time. */
  }
}

/** How much chart this device is holding, for the debug panel. */
export async function stats(): Promise<{ tiles: number; quota: number; usage: number }> {
  let tiles = 0;
  try {
    const d = await db();
    tiles = await new Promise<number>((resolve) => {
      const rq = d.transaction(STORE, 'readonly').objectStore(STORE).count();
      rq.onsuccess = () => resolve(rq.result);
      rq.onerror = () => resolve(0);
    });
  } catch {
    /* nothing held */
  }
  let quota = 0;
  let usage = 0;
  try {
    const e = await navigator.storage?.estimate?.();
    quota = e?.quota ?? 0;
    usage = e?.usage ?? 0;
  } catch {
    /* not available */
  }
  return { tiles, quota, usage };
}

export async function clear(): Promise<void> {
  try {
    const d = await db();
    d.transaction(STORE, 'readwrite').objectStore(STORE).clear();
  } catch {
    /* nothing to clear */
  }
}
